// src/components/layout/TopBar.js
import React, { useMemo } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Chip,
  Avatar,
  Tooltip,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BusinessIcon from "@mui/icons-material/Business";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const sanitizeTitle = (rawTitle) => {
  const t = String(rawTitle || "").trim();
  if (!t) return "Dashboard";

  const cleaned = t
    .replace(/\b(connexion|login)\b/gi, "")
    .replace(/\b(admin|agent|client)\b/gi, "")
    .replace(/[-–—|()]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

  return cleaned || "Dashboard";
};

const TopBar = ({ onMenuClick, title = "Dashboard" }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // phones
  const showNameInRow1 = useMediaQuery(theme.breakpoints.up("sm")); // tablets + desktop
  const showLogout = useMediaQuery(theme.breakpoints.up("md")); // md+ only

  // The authenticated user is authoritative. Do not display an agency
  // left in localStorage by a previous session.
  const agencyName =
    user?.agency_name || user?.agency || user?.location_city || user?.city || "";

  const displayName =
    user?.name || user?.full_name || user?.fullname || user?.username || "Utilisateur";

  const roleRaw = String(user?.role || "").trim().toLowerCase();

  const roleLabel = useMemo(() => {
    if (!roleRaw) return "";
    if (roleRaw === "admin") return "Admin";
    if (roleRaw === "agent") return "Agent";
    if (roleRaw === "client") return "Client";
    return user?.role || "";
  }, [roleRaw, user?.role]);

  const safeTitle = useMemo(() => sanitizeTitle(title), [title]);

  const initials = useMemo(() => {
    const parts = String(displayName).trim().split(/\s+/);
    const first = parts[0]?.[0] || "U";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
  }, [displayName]);

  const agencyPrefix = roleRaw === "admin" ? "Zone" : "Agence";

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // chips: transparent to avoid white blocks
  const chipSx = {
    color: "inherit",
    backgroundColor: "transparent",
    borderColor: "rgba(255,255,255,0.45)",
    fontWeight: 800,
    "& .MuiChip-icon": { color: "inherit" },
    "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
  };

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        backdropFilter: "blur(10px)",
      }}
    >
      <Toolbar sx={{ flexDirection: "column", alignItems: "stretch", py: 1, gap: 0.8 }}>
        {/* ===== Row 1 ===== */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {/* Left: Menu + Title + Agency */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
            <Tooltip title="Menu">
              <IconButton color="inherit" edge="start" onClick={onMenuClick}>
                <MenuIcon />
              </IconButton>
            </Tooltip>

            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              noWrap
              sx={{ fontWeight: 900, minWidth: 0 }}
            >
              {safeTitle}
            </Typography>

            {Boolean(agencyName) && (
              <Chip
                icon={<BusinessIcon />}
                label={`${agencyPrefix}: ${agencyName}`}
                size="small"
                variant="outlined"
                sx={{ ...chipSx, maxWidth: isMobile ? 170 : 280 }}
              />
            )}
          </Box>

          {/* Right: Tablet+Desktop name+role, Desktop adds logout inline */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            {showNameInRow1 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Avatar sx={{ width: 34, height: 34, fontWeight: 900 }}>{initials}</Avatar>
                <Typography variant="body2" noWrap sx={{ fontWeight: 900, maxWidth: 240 }}>
                  {displayName}
                </Typography>
              </Box>
            )}

            {Boolean(roleLabel) && (
              <Chip
                label={roleLabel}
                size="small"
                variant="outlined"
                sx={{ ...chipSx, maxWidth: 120 }}
              />
            )}

            {/* ✅ Logout INLINE on md+ */}
            {showLogout && (
              <Tooltip title="Déconnexion">
                <Button
                  color="inherit"
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2 }}
                >
                  Déconnexion
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* ===== Row 2 (phones only): name only) ===== */}
        {isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Avatar sx={{ width: 34, height: 34, fontWeight: 900 }}>{initials}</Avatar>
            <Typography variant="body2" noWrap sx={{ fontWeight: 900, minWidth: 0 }}>
              {displayName}
            </Typography>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
